import { injectable } from 'tsyringe';
import { kRobloxGameId } from 'app-core/src/constants/constants';
import { LoggerService } from 'app-core/src/services/logger.service';
import { RecommendationDatastoreService } from 'app-core/src/services/datastore-service/recommendation-datastore.service';
import { RobloxExperienceHistoryService } from 'app-core/src/services/roblox-experience-history/roblox-experience-history.service';
import { SubGameHandler } from './sub-game-handler';

// -----------------------------------------------------------------------------
@injectable()
export class RobloxSubGameHandler extends SubGameHandler {
  private readonly logger = LoggerService.getCategory('RobloxSubGameHandler');

  public readonly gameId = kRobloxGameId;

  // ---------------------------------------------------------------------------
  public constructor(
    datastore: RecommendationDatastoreService,
    private readonly experienceHistoryService: RobloxExperienceHistoryService,
  ) {
    super(datastore);
  }

  // ---------------------------------------------------------------------------
  // Returns only the configured sub-experiences the user has actually played,
  // ordered by total playtime (most-played first).
  public async getOrderedCandidates(): Promise<number[]> {
    const subGameIds = await this.readSubGameIdsFromConfig();
    if (subGameIds.length === 0) {
      this.logger.info(
        `No sub-game IDs configured for gameId ${this.gameId} — skipping`,
      );
      return [];
    }

    const candidateSet = new Set<number>(subGameIds);
    // getTopExperiences applies the 2-week cutoff and returns summaries
    // already ordered by playtime — we just keep the ones we care about.
    const recent = this.experienceHistoryService.getTopExperiences();
    const ordered: number[] = [];

    if (!recent || recent.length === 0) {
      return [];
    }

    for (const summary of recent) {
      const id = parseInt(summary.experienceId, 10);
      if (candidateSet.has(id)) {
        ordered.push(id);
      }
    }

    if (ordered.length === 0) {
      this.logger.info(
        `None of the ${subGameIds.length} configured sub-experiences ` +
          'have recent playtime — no candidates selected',
      );
      return [];
    }

    this.logger.info(
      `Selected ${ordered.length}/${subGameIds.length} sub-experience ` +
        `candidates (ordered by recent playtime): ${ordered.join(', ')}`,
    );

    return ordered;
  }
}
